import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { RunStatus, Interval } from '../../../../../../../backend/src/types/types';
import { useSubmitRunApproval } from '@/api/mutations/useSubmitRunApproval';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  PipelineProcessingStageDescriptions,
  PipelineProcessingStages,
  ProcessingStatus,
} from '@/new/lib/pipeline-status';
import { useMatterProcessingStore } from '@/new/stores/matter-processing-store';

function formatDurationEstimate(durationEstimate?: Interval | null): string {
  if (!durationEstimate) return '';
  const parts: string[] = [];
  const days = durationEstimate?.days ?? 0;
  const hours = durationEstimate?.hours ?? 0;
  const minutes = durationEstimate?.minutes ?? 0;
  if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
  return parts.join(' ');
}

const getPipelineStatusIcon = (pipelineStatus: ProcessingStatus, index: number) => {
  switch (true) {
    case PipelineProcessingStages.indexOf(pipelineStatus) > index:
      return (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{
            type: 'spring',
            stiffness: 260,
            damping: 20,
          }}
          className="flex items-center justify-center"
        >
          <Icon className="lucide--circle-check text-teal-500" />
        </motion.div>
      );
    case PipelineProcessingStages.indexOf(pipelineStatus) === index:
      return <Icon className="lucide--loader-circle animate-spin text-blue-500 duration-1000" />;
  }
};

export const ProcessingPipeline = ({
  casePipelineStatus,
  caseId,
}: {
  casePipelineStatus: RunStatus;
  caseId?: string;
}) => {
  const { t } = useTranslation();
  const { matterId } = useParams();
  const { mutateAsync: submitRunApproval, isLoading: isSubmittingApproval } = useSubmitRunApproval(
    caseId ?? '',
    matterId,
  );
  const processingState = useMatterProcessingStore((state) => state.getProcessingState(matterId ?? ''));
  // Use the first processing run (already sorted with awaiting approval first)
  const mostRecentRun = processingState?.allProcessingRuns?.[0] ?? null;

  const handleApprove = async () => {
    if (!mostRecentRun) return;
    await submitRunApproval({ runId: mostRecentRun.id, approvalGranted: true });
  };

  const handleDeny = async () => {
    if (!mostRecentRun) return;
    await submitRunApproval({ runId: mostRecentRun.id, approvalGranted: false });
  };

  const credits =
    (mostRecentRun?.totalPages as number | undefined) ??
    (mostRecentRun?.successfulPagesCount || 0) + (mostRecentRun?.failedPagesCount || 0);

  // If awaiting approval, show only the approval UI
  if (casePipelineStatus === RunStatus.AwaitingApproval) {
    return (
      <>
        <div className="flex w-full items-center justify-between rounded-t-lg border-b border-gray-100 bg-gray-50 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 ring-1 ring-blue-100">
              <Icon className="lucide--file-cog text-base text-blue-600" />
            </div>
            <h3 className="text-base font-medium text-gray-900">Processing Estimate</h3>
          </div>
        </div>

        <div className="flex flex-col gap-6 p-6">
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-3 rounded-md border border-dashed border-blue-200 bg-blue-50 px-4 py-2 shadow-sm">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100">
                <Icon className="lucide--file text-base text-blue-600" />
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900">{credits.toLocaleString?.() ?? credits}</div>
                <div className="text-xs text-gray-500">Total Credits</div>
              </div>
            </div>

            {mostRecentRun?.durationEstimate && (
              <div className="flex items-center gap-2 rounded-md border border-dashed border-purple-200 bg-purple-50 px-4 py-2 shadow-sm">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100">
                  <Icon className="lucide--file-clock text-base text-purple-600" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">
                    {formatDurationEstimate(mostRecentRun.durationEstimate)}
                  </div>
                  <div className="text-xs text-gray-500">Estimated Processing Time</div>
                </div>
              </div>
            )}
          </div>

          <div className="border-gray-00 flex items-start gap-2 rounded-md border border-dashed bg-gray-50 p-3">
            <div className="text-xs text-gray-600">
              <p className="mb-1 font-medium text-gray-800 underline">Why am I seeing this?</p>
              <p>
                This run contains <b>{credits.toLocaleString?.() ?? credits}</b> {credits === 1 ? 'credit' : 'credits'}.
                Your {t('organisation')}'s configuration means that you need to confirm that you want to proceed before
                processing can continue.
              </p>
              <p className="mt-2 italic">
                If you wish to adjust these limits, contact your administrator, or if you are an administrator, they can
                be changed{' '}
                <Link
                  to="/organisation-settings/credits"
                  className="text-blue-500 underline hover:cursor-pointer hover:text-blue-600"
                >
                  here
                </Link>
                .
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="primary"
              onClick={handleApprove}
              disabled={isSubmittingApproval}
              className="flex-1"
              size="default"
            >
              <Icon className="lucide--check" />
              <span>Continue</span>
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeny}
              disabled={isSubmittingApproval}
              className="flex-1"
              size="default"
            >
              <Icon className="lucide--x" />
              <span>Cancel</span>
            </Button>
          </div>
        </div>
      </>
    );
  }

  // Otherwise show the processing pipeline
  const stages = PipelineProcessingStages.filter((status) => status !== RunStatus.AwaitingApproval);

  return (
    <div className="p-6">
      <div className="relative">
        {/* Continuous horizontal line through all stages */}
        <div className="absolute top-3 right-0 left-0 h-[2px] bg-gray-200" style={{ zIndex: 0 }} />

        {/* Stage icons and labels */}
        <div className="relative flex items-start justify-between" style={{ zIndex: 1 }}>
          {stages.map((status: ProcessingStatus, idx) => {
            const currentIndex = PipelineProcessingStages.indexOf(casePipelineStatus as ProcessingStatus);
            const stageIndex = PipelineProcessingStages.indexOf(status);
            const isActive = stageIndex === currentIndex;
            const isCompleted = stageIndex < currentIndex;

            return (
              <div key={status} className="flex flex-1 flex-col items-center">
                <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-gray-300 bg-white">
                  {getPipelineStatusIcon(casePipelineStatus as ProcessingStatus, stageIndex)}
                </div>
                <div
                  className={`mt-3 rounded px-3 py-1 text-center text-xs ${
                    isActive
                      ? 'bg-blue-100 text-blue-700'
                      : isCompleted
                        ? 'bg-brandSecondary/20 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {PipelineProcessingStageDescriptions[status].title}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
